<div class="settings-modal__overlay" (click)="closeSettings()">
  <section class="settings-modal" (click)="$event.stopPropagation()">
    <div class="settings-modal__header">
      <h2 class="settings-modal__title">{{ 'Settings' | aceTranslate }}</h2>
      <button type="button" class="settings-modal__close" (click)="closeSettings()" [attr.aria-label]="'Close' | aceTranslate">
        <i class="ri-close-line" aria-hidden="true"></i>
      </button>
    </div>

    <div class="settings-modal__body">
      <nav class="settings-modal__sidebar" [attr.aria-label]="'Settings' | aceTranslate">
        <button
          *ngFor="let tab of tabs"
          type="button"
          class="settings-modal__tab"
          [class.settings-modal__tab--active]="activeTab === tab.id"
          (click)="setActiveTab(tab.id)">
          {{ tab.label | aceTranslate }}
        </button>
      </nav>

      <div class="settings-modal__content">
        <div *ngIf="isLoading" class="settings-modal__loading">
          <div class="settings-modal__spinner" role="status"></div>
          <p>{{ 'Loading...' | aceTranslate }}</p>
        </div>

        <div *ngIf="loadError && !isLoading" class="settings-modal__error-state">
          <span><i class="ri-error-warning-line me-2"></i>{{ loadError }}</span>
          <app-button
            buttonMainStyle="btn btn-primary"
            buttonType="button"
            [buttonText]="'Retry' | aceTranslate"
            (btnClick)="loadLanguages()">
          </app-button>
        </div>

        <ng-container *ngIf="!isLoading && !loadError">
          <div *ngIf="activeTab === 'general'" class="settings-general">
            <label class="settings-field">
              <span>{{ 'Speaker Language' | aceTranslate }}</span>
              <select class="form-select" [(ngModel)]="formState.speakerLanguage">
                <option value="">{{ 'Select speaker language' | aceTranslate }}</option>
                <option value="auto">{{ 'Auto Detect' | aceTranslate }}</option>
                <option *ngFor="let language of languageOptions" [value]="language.code">
                  {{ language.name }}
                </option>
              </select>
            </label>

            <label class="settings-field">
              <span>{{ 'Avatar Language' | aceTranslate }}</span>
              <select
                class="form-select"
                [ngModel]="formState.avatarLanguage"
                (ngModelChange)="onAvatarLanguageChange($event)">
                <option value="">{{ 'Select avatar language' | aceTranslate }}</option>
                <option *ngFor="let language of languageOptions" [value]="language.code">
                  {{ language.name }}
                </option>
              </select>
            </label>

            <label class="settings-field">
              <span>{{ 'Avatar Dialect' | aceTranslate }}</span>
              <select
                class="form-select"
                [(ngModel)]="selectedAvatarDialect"
                [disabled]="!avatarDialectEnabled">
                <option value="" disabled>{{ 'Only for Arabic language' | aceTranslate }}</option>
                <option *ngFor="let dialect of avatarDialects" [value]="dialect.value">
                  {{ dialect.label | aceTranslate }}
                </option>
              </select>
            </label>

            <label class="settings-field">
              <span>{{ 'System Language' | aceTranslate }}</span>
              <select class="form-select" [(ngModel)]="formState.systemLanguage">
                <option *ngFor="let language of systemLanguages" [value]="language.code">
                  {{ language.flag }} {{ language.label | aceTranslate }}
                </option>
              </select>
            </label>
          </div>

          <div *ngIf="activeTab === 'accessibility'" class="settings-accessibility">
            <p class="settings-accessibility__subtitle">
              {{ 'Fine-tune reading comfort across the instructor portal.' | aceTranslate }}
            </p>

            <div class="settings-accessibility__grid">
              <button
                type="button"
                class="settings-accessibility__card"
                (click)="cycleAccessibilityLevel('textSpacing', textSpacingLevelCount)">
                <div class="settings-accessibility__card-header">
                  <span class="settings-accessibility__card-icon">
                    <i class="ri-expand-left-right-line" aria-hidden="true"></i>
                  </span>
                  <span class="settings-accessibility__card-label">{{ 'Text Spacing' | aceTranslate }}</span>
                </div>

                <div
                  class="settings-accessibility__preview"
                  [attr.data-a11y-preview-text-spacing]="accessibilitySettings.textSpacing">
                  {{ 'Reading sample text' | aceTranslate }}
                </div>

                <div class="settings-accessibility__card-footer">
                  <span class="settings-accessibility__card-meta">
                    {{ getAccessibilityLevelLabel(accessibilitySettings.textSpacing, textSpacingLevelCount) }}
                  </span>
                  <div class="settings-accessibility__progress">
                    <span
                      *ngFor="let dot of textSpacingProgress; let i = index"
                      class="settings-accessibility__progress-dot"
                      [class.settings-accessibility__progress-dot--filled]="i <= accessibilitySettings.textSpacing">
                    </span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                class="settings-accessibility__card"
                (click)="cycleAccessibilityLevel('bigText', bigTextLevelCount)">
                <div class="settings-accessibility__card-header">
                  <span class="settings-accessibility__card-icon">
                    <i class="ri-font-size-2" aria-hidden="true"></i>
                  </span>
                  <span class="settings-accessibility__card-label">{{ 'Bigger Text' | aceTranslate }}</span>
                </div>

                <div
                  class="settings-accessibility__preview"
                  [attr.data-a11y-preview-big-text]="accessibilitySettings.bigText">
                  {{ 'Reading sample text' | aceTranslate }}
                </div>

                <div class="settings-accessibility__card-footer">
                  <span class="settings-accessibility__card-meta">
                    {{ getAccessibilityLevelLabel(accessibilitySettings.bigText, bigTextLevelCount) }}
                  </span>
                  <div class="settings-accessibility__progress">
                    <span
                      *ngFor="let dot of bigTextProgress; let i = index"
                      class="settings-accessibility__progress-dot"
                      [class.settings-accessibility__progress-dot--filled]="i <= accessibilitySettings.bigText">
                    </span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                class="settings-accessibility__card"
                (click)="cycleAccessibilityLevel('lineHeight', lineHeightLevelCount)">
                <div class="settings-accessibility__card-header">
                  <span class="settings-accessibility__card-icon">
                    <i class="ri-line-height" aria-hidden="true"></i>
                  </span>
                  <span class="settings-accessibility__card-label">{{ 'Line Height' | aceTranslate }}</span>
                </div>

                <div
                  class="settings-accessibility__preview"
                  [attr.data-a11y-preview-line-height]="accessibilitySettings.lineHeight">
                  {{ 'Comfortable line spacing makes longer paragraphs easier to read.' | aceTranslate }}
                </div>

                <div class="settings-accessibility__card-footer">
                  <span class="settings-accessibility__card-meta">
                    {{ getAccessibilityLevelLabel(accessibilitySettings.lineHeight, lineHeightLevelCount) }}
                  </span>
                  <div class="settings-accessibility__progress">
                    <span
                      *ngFor="let dot of lineHeightProgress; let i = index"
                      class="settings-accessibility__progress-dot"
                      [class.settings-accessibility__progress-dot--filled]="i <= accessibilitySettings.lineHeight">
                    </span>
                  </div>
                </div>
              </button>

              <button
                type="button"
                class="settings-accessibility__card"
                (click)="toggleDyslexiaFont()">
                <div class="settings-accessibility__card-header">
                  <span class="settings-accessibility__card-icon">
                    <i class="ri-book-open-line" aria-hidden="true"></i>
                  </span>
                  <span class="settings-accessibility__card-label">{{ 'Dyslexia Font' | aceTranslate }}</span>
                </div>

                <div
                  class="settings-accessibility__preview"
                  [attr.data-a11y-preview-dyslexia]="accessibilitySettings.dyslexiaFont ? 'on' : 'off'">
                  {{ 'An accessible typeface designed for clearer reading.' | aceTranslate }}
                </div>

                <div class="settings-accessibility__card-footer">
                  <span class="settings-accessibility__card-meta">
                    {{ (accessibilitySettings.dyslexiaFont ? 'On' : 'Off') | aceTranslate }}
                  </span>
                  <span
                    class="settings-accessibility__toggle"
                    [class.settings-accessibility__toggle--on]="accessibilitySettings.dyslexiaFont">
                    <span class="settings-accessibility__toggle-knob"></span>
                  </span>
                </div>
              </button>
            </div>

            <div class="settings-accessibility__footer">
              <button type="button" class="settings-accessibility__reset" (click)="resetAccessibility()">
                <i class="ri-reset-left-line" aria-hidden="true"></i>
                {{ 'Reset' | aceTranslate }}
              </button>
            </div>
          </div>
        </ng-container>
      </div>
    </div>

    <div class="settings-modal__footer">
      <app-button
        buttonMainStyle="btn btn-primary"
        buttonType="button"
        [buttonText]="(isSaving ? 'Saving...' : 'Save') | aceTranslate"
        [disabled]="isSaving || isLoading || !!loadError"
        (btnClick)="save()">
      </app-button>
    </div>
  </section>
</div>
